import { Box, Button, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { ProjectListDto } from "~/api/GeneratedApi";
import FileUpload from "~/components/FileUpload";
import { CircularLoadingWithLabel } from "../components/CircularLoadingWithLabel";
import { useProjectDetails } from "../hooks/useProjectDetails";
import { useProjectShapeFile } from "../hooks/useProjectShapeFile";
import {
  UploadFolderType,
  useProjectShapeUpload,
} from "../hooks/useProjectShapeUpload";
import { UploadedFilesContainer } from "./UploadedFilesContainer";

export type FileExtensionNames = "shp" | "shx" | "dbf" | "prj";

export type ReceivedFiles = Record<string, { files: File[] }>;

export type DividedReceivedFileExtensions = Record<
  "allowedFiles" | "invalidFiles",
  ReceivedFiles
>;

export interface ProjectEpsgAutoFillType {
  coordinateSystemId: number | null;
}
export const allowedFileExtension = ["shp", "shx", "dbf", "prj"] as const;
export const requiredFileExtension = ["shp", "shx", "dbf"] as const;
const allowedFiles: ReceivedFiles = {};
allowedFileExtension.forEach((v) => {
  allowedFiles[v] = { files: [] };
});

type ProjectUploadContentProps = {
  id: number;
  uploadFolderType: UploadFolderType;
  projectData: ProjectListDto;
};

export const ProjectUploadContent = ({
  id,
  uploadFolderType,
  projectData,
}: ProjectUploadContentProps) => {
  const { mutateShapeFiles } = useProjectShapeUpload();
  const [allowedFilesState, setAllowedFiles] =
    React.useState<ReceivedFiles>(allowedFiles);
  const [invalidFilesState, setInvalidFiles] = React.useState<ReceivedFiles>(
    {},
  );
  const [projectionId, setProjectionId] = React.useState<null | number>(null);

  const setFiles = React.useCallback(
    (files: File[]) => {
      const allFiles: DividedReceivedFileExtensions = {
        allowedFiles: { ...allowedFilesState },
        invalidFiles: { ...invalidFilesState },
      };
      files.forEach((file) => {
        const fileNameArr = file.name.split(".");
        const fileExtension = fileNameArr[fileNameArr.length - 1];
        const t = allowedFileExtension.includes(
          fileExtension as typeof allowedFileExtension[number],
        )
          ? "allowedFiles"
          : "invalidFiles";

        let uploadFiles = allFiles[t][fileExtension];

        const duplicate = uploadFiles?.files.find((f) => f.name === file.name);
        if (!uploadFiles?.files?.length) {
          uploadFiles = { files: [file] };
        } else if (duplicate) {
          uploadFiles = {
            files: uploadFiles.files.map((f) =>
              f.name === duplicate.name ? file : f,
            ),
          };
        } else {
          uploadFiles = {
            files: [...uploadFiles.files, file],
          };
        }
        allFiles[t][fileExtension] = uploadFiles;
      });

      setAllowedFiles(allFiles.allowedFiles);
      setInvalidFiles(allFiles.invalidFiles);

      return allFiles;
    },
    [allowedFilesState, invalidFilesState],
  );

  // check that every required extension has same amount of files and has more than 1 file

  const invalidFileAmount = React.useMemo(() => {
    for (let i = 0; i < requiredFileExtension.length - 1; i += 1) {
      const curFileExtension = requiredFileExtension[i];
      const nextFileExtension = requiredFileExtension[i + 1];
      const curFiles = allowedFilesState[curFileExtension].files;
      const nextFiles = allowedFilesState[nextFileExtension].files;
      if (curFiles.length !== nextFiles.length || nextFiles.length === 0) {
        return true;
      }
    }
    return false;
  }, [allowedFilesState]);

  const buttonDisabled = React.useMemo(
    () =>
      requiredFileExtension.some(
        (d) => allowedFilesState[d]?.files.length < 1,
      ) ||
      (allowedFilesState.prj.files.length !== 1 && !projectionId) ||
      invalidFileAmount,
    [allowedFilesState, invalidFileAmount, projectionId],
  );

  const handleFileAdd = React.useCallback(() => {
    const fileFormat: File[] = [];
    Object.keys(allowedFilesState).forEach((key) => {
      if (allowedFilesState[key].files.length) {
        fileFormat.push(...allowedFilesState[key].files);
      }
    });

    mutateShapeFiles({
      projectId: id,
      files: fileFormat,
      projectionId,
      uploadFolderType,
    });
  }, [allowedFilesState, mutateShapeFiles, id, projectionId, uploadFolderType]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateRows: "minmax(50% , 1fr) auto",
        width: "100%",
        height: { xs: "100vh", md: "70vh" },
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* <LoadingBackdrop text={t("projects:processFiles")} open={isLoading} /> */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FileUpload options={{ onDrop: (x: File[]) => setFiles(x) }} />
      </Box>
      <Box sx={{ overflowY: "auto" }}>
        <UploadedFilesContainer
          setAllowedFiles={setAllowedFiles}
          setInvalidFiles={setInvalidFiles}
          allowedFiles={allowedFilesState}
          invalidFiles={invalidFilesState}
          setProjectionId={setProjectionId}
          projectionId={projectionId}
          invalidFileAmount={invalidFileAmount}
        />
      </Box>
      <FooterContainer
        processingFiles={projectData.processingFiles}
        isDisabled={buttonDisabled}
        onClick={handleFileAdd}
      />
    </Box>
  );
};

type FooterContainerProps = {
  isDisabled: boolean;
  onClick: () => void;
  processingFiles: boolean;
};

function FooterContainer({
  isDisabled,
  onClick,
  processingFiles,
}: FooterContainerProps) {
  const { t } = useTranslation("file-explorer");
  const { activeTab, id } = useProjectDetails();

  const { progressJson, planJson } = useProjectShapeFile({ id });

  const infoText = React.useMemo(() => {
    if (processingFiles)
      return <CircularLoadingWithLabel text={t("projects:processingFiles")} />;
    if (activeTab === "upload-planning" && planJson?.name) {
      return (
        <Typography noWrap component="span">
          {t("projects:planName")}: {planJson?.name}
        </Typography>
      );
    }
    if (
      progressJson?.features?.[progressJson.features.length - 1]?.properties &&
      activeTab === "upload-progress"
    )
      return (
        <Typography noWrap component="span">
          {t("projects:progressName")}: {progressJson.name}
        </Typography>
      );
    return null;
  }, [
    processingFiles,
    t,
    activeTab,
    planJson?.name,
    progressJson?.features,
    progressJson?.name,
  ]);

  return (
    <Box
      sx={{
        marginLeft: "auto",
        gap: "5px",
        display: "flex",
        alignItems: "center",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {infoText}
      {processingFiles ? null : (
        <Button
          sx={{ marginLeft: "5px" }}
          variant="contained"
          color="secondary"
          disabled={isDisabled}
          onClick={onClick}
        >
          {t("general:add")}
        </Button>
      )}
    </Box>
  );
}
